/**
 * Created by neo on 21.07.23
 */
import { makeAutoObservable } from 'mobx';
import { LocalizedEntity, createLocalizedEntityFields } from '../../LocalizedEntity';

export type RecurringPatternType = 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';

export type ActivationFrequencyJson = {
  duration?: string;
  type?: RecurringPatternType;
  maxActivations?: number;
};

export type ActivationFrequency = {
  duration?: string;
  type?: RecurringPatternType;
  maxActivations: number;
  toJS(): ActivationFrequencyJson;
};

export function createActivationFrequency(json?: ActivationFrequencyJson): ActivationFrequency {
  return makeAutoObservable({
    duration: json?.duration,
    type: json?.type,
    maxActivations: json?.maxActivations ?? 1,

    toJS(): ActivationFrequencyJson {
      return {
        duration: this.duration,
        type: this.type,
        maxActivations: this.maxActivations,
      };
    },
  });
}

export type PointsModelRule = LocalizedEntity & {
  type: string;
  totalPossiblePointsToday: number;
  collectedPointsToday: number;
  totalExperiencePoints: number;
  totalRewardCoins: number;
  collectedRewardCoins: number;
  collectedExperiencePoints: number;
  completed: boolean;
  canBeCompleted: boolean;
  allowInChallenge: boolean;
  appActionLink?: string;
  activationFrequencies: ActivationFrequency[];
  readonly dailyActivationFrequency: ActivationFrequency | undefined;
  readonly maxDailyActivations: number;
  readonly pointsPerActivation: number;
  readonly currentActivationCount: number;
};

export function createPointsModelRule(json: PointsModelRule): PointsModelRule {
  const base = createLocalizedEntityFields(json);
  return makeAutoObservable({
    ...base,
    type: json?.type ?? 'simple',
    completed: json?.completed ?? false,
    canBeCompleted: json?.canBeCompleted ?? false,
    totalPossiblePointsToday: json?.totalPossiblePointsToday ?? 0,
    collectedPointsToday: json?.collectedPointsToday ?? 0,
    totalRewardCoins: json?.totalRewardCoins ?? 0,
    totalExperiencePoints: json?.totalExperiencePoints ?? 0,
    collectedRewardCoins: json?.collectedRewardCoins ?? 0,
    collectedExperiencePoints: json?.collectedExperiencePoints ?? 0,
    allowInChallenge: json?.allowInChallenge ?? false,
    appActionLink: json?.appActionLink,
    activationFrequencies: (json?.activationFrequencies ?? []).map((f) => createActivationFrequency(f)),

    get dailyActivationFrequency(): ActivationFrequency | undefined {
      return this.activationFrequencies.find((f) => f.type === 'daily');
    },

    get maxDailyActivations(): number {
      return this.dailyActivationFrequency?.maxActivations ?? 1;
    },

    get pointsPerActivation(): number {
      if (this.maxDailyActivations <= 1) return this.totalPossiblePointsToday;
      return Math.floor(this.totalPossiblePointsToday / this.maxDailyActivations);
    },

    get currentActivationCount(): number {
      if (this.pointsPerActivation <= 0) return 0;
      return Math.floor(this.collectedPointsToday / this.pointsPerActivation);
    },
  });
}
